import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Users, User, CreditCard, Eye, Edit, FileText, TrendingUp, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FamilyManagement = ({ user, onLogout }) => {
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [owners, setOwners] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  
  const [familyForm, setFamilyForm] = useState({
    family_name: '',
    family_head: '',
    rm_id: user?.id || ''
  });
  
  const [ownerForm, setOwnerForm] = useState({
    family_id: '',
    owner_name: '',
    pan: '',
    mobile: '',
    email: '',
    date_of_birth: ''
  });
  
  const [accountForm, setAccountForm] = useState({
    owner_id: '',
    account_name: '',
    account_number: '',
    account_type: 'investment',
    advisor_name: '',
    is_held_away: false
  });

  useEffect(() => {
    fetchFamilies();
  }, []);

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/families`, config);
      // Handle Wealth Spectrum API response format
      setFamilies(response.data.families || response.data);
    } catch (error) {
      console.error('Error fetching families:', error);
      toast.error('Failed to fetch families from Wealth Spectrum');
    } finally {
      setLoading(false);
    }
  };

  const fetchOwners = async (familyId) => {
    setOwnersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      console.log(`Fetching owners for family: ${familyId}`);
      const response = await axios.get(`${API}/owners/family/${familyId}`, config);
      
      // Handle Wealth Spectrum API response format
      const ownersData = response.data.owners || response.data;
      console.log(`Received ${ownersData.length} owners for family ${familyId}`);
      
      setOwners(ownersData);
    } catch (error) {
      console.error('Error fetching owners:', error);
      toast.error('Failed to fetch owners from Wealth Spectrum');
      setOwners([]);
    } finally {
      setOwnersLoading(false);
    }
  };

  const fetchAccounts = async (ownerId) => {
    setAccountsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      console.log(`Fetching accounts for owner: ${ownerId}`);
      const response = await axios.get(`${API}/accounts/owner/${ownerId}`, config);
      
      // Handle Wealth Spectrum API response format
      const accountsData = response.data.accounts || response.data;
      console.log(`Received ${accountsData.length} accounts for owner ${ownerId}`);
      
      setAccounts(accountsData);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to fetch accounts from Wealth Spectrum');
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const handleRefreshData = () => {
    // Invalidate Wealth Spectrum cache and refresh data
    toast.info('Refreshing data from Wealth Spectrum...');
    
    // Reset all state
    setSelectedFamily(null);
    setOwners([]);
    setAccounts([]);
    
    // Fetch fresh data
    fetchFamilies();
  };

  const handleSyncWithWealthSpectrum = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Call cache invalidation endpoint
      await axios.post(`${API}/wealth-spectrum/sync`, {}, config);
      toast.success('Data synchronized with Wealth Spectrum');
      fetchFamilies();
    } catch (error) {
      console.error('Error syncing with Wealth Spectrum:', error);
      toast.error('Failed to sync with Wealth Spectrum');
    }
  };

  const handleFamilySelect = async (family) => {
    setSelectedFamily(family);
    setOwners([]); // Clear previous owners
    setAccounts([]); // Clear previous accounts
    await fetchOwners(family.id);
  };

  const handleOwnerSelect = async (owner) => {
    setAccounts([]); // Clear previous accounts
    await fetchAccounts(owner.id);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd87a]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              Family Management
            </h1>
            <p className="text-gray-600">
              Real-time data from{' '}
              <a 
                href="https://www.wealthspectrum.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-ugmonk-dark hover:text-[#f5cc6a] font-medium"
              >
                Wealth Spectrum
              </a>
              {' '}platform
            </p>
          </div>
          
          {/* Sync and Refresh Actions */}
          <div className="flex gap-2">
            <Button 
              onClick={handleRefreshData}
              variant="outline" 
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={handleSyncWithWealthSpectrum}
              className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
            >
              <Users className="w-4 h-4 mr-2" />
              Sync WS
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Families List */}
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-ugmonk-dark" />
                Families ({families.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-96 overflow-y-auto">
              {families.map((family) => (
                <div
                  key={family.id}
                  onClick={() => handleFamilySelect(family)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedFamily?.id === family.id
                      ? 'bg-[#fffdf0] border-[#ffd87a]/50'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <div className="font-semibold text-gray-900 text-sm">{family.family_name}</div>
                  <div className="text-xs text-gray-600">Head: {family.family_head || 'Not specified'}</div>
                  <div className="text-xs text-ugmonk-dark">AUM: ₹{family.total_aum?.toLocaleString() || '0'}</div>
                </div>
              ))}
              {families.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No families found</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Owners List */}
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5 text-ugmonk-dark" />
                Owners ({owners.length})
                {selectedFamily && (
                  <span className="text-xs bg-[#fffdf0] text-ugmonk-dark px-2 py-1 rounded-full ml-auto">
                    Wealth Spectrum
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-96 overflow-y-auto">
              {selectedFamily ? (
                ownersLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a1a] mx-auto mb-3"></div>
                    <p className="text-gray-500">Loading owners...</p>
                  </div>
                ) : owners.length > 0 ? (
                  owners.map((owner) => (
                    <div
                      key={owner.id}
                      className="p-3 rounded-lg border bg-gray-50 border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => handleOwnerSelect(owner)}
                    >
                      <div className="font-semibold text-gray-900 text-sm">{owner.owner_name}</div>
                      <div className="text-xs text-gray-600">PAN: {owner.pan}</div>
                      <div className="text-xs text-gray-600">Mobile: {owner.mobile}</div>
                      <div className="text-xs text-ugmonk-dark">AUM: ₹{owner.total_aum?.toLocaleString() || '0'}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No owners found in {selectedFamily.family_name}</p>
                  </div>
                )
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a family to view owners</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Accounts List */}
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-ugmonk-dark" />
                Accounts ({accounts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-96 overflow-y-auto">
              {accountsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffd87a] mx-auto mb-3"></div>
                  <p className="text-gray-500">Loading accounts...</p>
                </div>
              ) : accounts.length > 0 ? (
                accounts.map((account) => (
                  <div
                    key={account.id}
                    className="p-3 rounded-lg border bg-gray-50 border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <div className="font-semibold text-gray-900 text-sm">{account.account_name}</div>
                    <div className="text-xs text-gray-600">Type: {account.account_type}</div>
                    <div className="text-xs text-gray-600">Number: {account.account_number}</div>
                    {account.is_held_away && (
                      <div className="text-xs text-ugmonk-dark font-medium">⚠️ Held Away Account</div>
                    )}
                    <div className="text-xs text-ugmonk-dark font-semibold">Value: ₹{account.total_value?.toLocaleString() || '0'}</div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select an owner to view accounts</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default FamilyManagement;
